import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import socket from '../socket';
import useSocketEvent from '../utils/useSocketEvent';
import { fetchJson } from '../utils/api';
import {
    createOrUpdatePlayset,
    loadActiveShow,
    loadQuestions,
    loadShowFinale,
    loadShowMatrix,
    saveShowFinale,
    setActiveShow,
} from '../integrations/quizApiAdapter';
import MatrixGrid from './MatrixGrid';
import './css/ShowEditView.css';

const FINALE_KINDS = [
    { value: 'decoder', label: 'Decoder' },
    { value: 'quizpoker', label: 'Quizpoker' },
    { value: 'finalfrage', label: 'Finalfrage' },
];

function ShowEditView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [show, setShow] = useState(null);
    const [activePlaysetId, setActivePlaysetId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [matrix, setMatrix] = useState(null);
    const [finale, setFinale] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const [nameInput, setNameInput] = useState('');
    const [descriptionInput, setDescriptionInput] = useState('');
    const [finaleKindInput, setFinaleKindInput] = useState('decoder');
    const [saveStatus, setSaveStatus] = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [showData, activeData, questionsData, matrixData, finaleData] = await Promise.all([
                fetchJson(`/api/playsets/${id}`),
                loadActiveShow(),
                loadQuestions(),
                loadShowMatrix(id).catch(() => null),
                loadShowFinale(id).catch(() => null),
            ]);
            setShow(showData);
            setActivePlaysetId(activeData?.playsetId ?? null);
            setQuestions(Array.isArray(questionsData) ? questionsData : []);
            setMatrix(Array.isArray(matrixData) ? matrixData : (matrixData?.matrix ?? null));
            setFinale(finaleData || null);

            setNameInput(showData?.name ?? '');
            setDescriptionInput(showData?.description ?? '');
            const kind = finaleData?.kind || showData?.stats?.finale?.kind || 'decoder';
            setFinaleKindInput(kind);
        } catch (err) {
            setError(err?.message || 'Fehler beim Laden der Show.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const onShowActiveChange = useCallback((payload) => {
        setActivePlaysetId(payload?.playsetId ?? null);
    }, []);
    useSocketEvent(socket, 'show:active', onShowActiveChange);

    const isActive = show && String(show.id) === String(activePlaysetId);

    const questionsForShow = useMemo(() => {
        if (!id) return [];
        const target = String(id);
        return questions.filter((q) =>
            Array.isArray(q.playsetIds) && q.playsetIds.map(String).includes(target)
        );
    }, [questions, id]);

    const matrixStats = useMemo(() => {
        if (!Array.isArray(matrix) || matrix.length === 0) {
            return { rows: 0, cols: 0, filled: 0 };
        }
        const rows = matrix.length;
        const cols = matrix[0]?.length || 0;
        let filled = 0;
        matrix.forEach((row) => row.forEach((cell) => { if (cell) filled += 1; }));
        return { rows, cols, filled };
    }, [matrix]);

    const handleActivate = async () => {
        if (!show) return;
        setBusy(true);
        try {
            const res = await setActiveShow(show.id);
            setActivePlaysetId(res?.playsetId ?? String(show.id));
        } catch (err) {
            setError(err?.message || 'Fehler beim Aktivieren.');
        } finally {
            setBusy(false);
        }
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!show) return;
        setBusy(true);
        setSaveStatus('');
        try {
            const updated = await createOrUpdatePlayset({
                id: show.id,
                name: nameInput.trim(),
                description: descriptionInput.trim() || null,
                status: show.status,
            });
            setShow((prev) => ({ ...prev, ...updated }));

            const currentKind = finale?.kind || show.stats?.finale?.kind || 'decoder';
            if (finaleKindInput && finaleKindInput !== currentKind) {
                const updatedFinale = await saveShowFinale(show.id, { kind: finaleKindInput });
                setFinale(updatedFinale || { kind: finaleKindInput });
            }

            setSaveStatus('Gespeichert.');
            setTimeout(() => setSaveStatus(''), 2500);
        } catch (err) {
            setError(err?.message || 'Fehler beim Speichern.');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="page text-muted">Show wird geladen...</div>;
    }

    if (error && !show) {
        return (
            <div className="page section-stack">
                <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                    {error}
                </div>
                <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard/shows')}>
                    Zurück zur Übersicht
                </button>
            </div>
        );
    }

    return (
        <div className="page section-stack">
            <div className="card section-stack">
                <div className="card-header" style={{ flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                        <Link to="/dashboard/shows" className="text-muted" style={{ textDecoration: 'none' }}>
                            ← Shows
                        </Link>
                        <h2 className="card-title" style={{ marginTop: '4px' }}>{show.name}</h2>
                        {show.description && <div className="text-muted">{show.description}</div>}
                    </div>
                    <div className="button-row">
                        {isActive ? (
                            <span className="chip">Aktiv</span>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleActivate}
                                disabled={busy}
                            >
                                Diese Show aktivieren
                            </button>
                        )}
                    </div>
                </div>

                {!isActive && (
                    <div
                        style={{
                            background: '#fff8e1',
                            color: '#7a5d00',
                            padding: '10px 12px',
                            borderRadius: '4px',
                        }}
                    >
                        Diese Show ist nicht aktiv. Erst nach dem Aktivieren wirken sich Änderungen
                        live auf die ShowPage aus.
                    </div>
                )}

                {error && (
                    <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                        {error}
                    </div>
                )}
            </div>

            {/* Section 1: Bearbeiten */}
            <form className="card section-stack" onSubmit={handleSave}>
                <div className="card-header">
                    <h3 className="card-title">Show bearbeiten</h3>
                    <span className="text-muted">Grundeinstellungen dieser Show.</span>
                </div>
                <div className="input-group">
                    <label htmlFor="show-name">Showname</label>
                    <input
                        id="show-name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        required
                    />
                </div>
                <div className="input-group">
                    <label htmlFor="show-description">Beschreibung</label>
                    <input
                        id="show-description"
                        type="text"
                        value={descriptionInput}
                        onChange={(e) => setDescriptionInput(e.target.value)}
                        placeholder="optional"
                    />
                </div>
                <div className="input-group">
                    <label htmlFor="show-finale-kind">Finalspiel</label>
                    <select
                        id="show-finale-kind"
                        value={finaleKindInput}
                        onChange={(e) => setFinaleKindInput(e.target.value)}
                    >
                        {FINALE_KINDS.map((k) => (
                            <option key={k.value} value={k.value}>{k.label}</option>
                        ))}
                    </select>
                    <span className="text-muted" style={{ fontSize: '0.85em' }}>
                        Inhalte (Phrasen, Decoder etc.) werden im Bereich „Finale“ in der Navigation bearbeitet.
                    </span>
                </div>
                <div className="button-row">
                    <button type="submit" className="btn btn-primary" disabled={busy || !nameInput.trim()}>
                        {busy ? 'Speichere...' : 'Speichern'}
                    </button>
                    {saveStatus && <span className="text-muted">{saveStatus}</span>}
                </div>
            </form>

            {/* Section 2: Fragen (read-only) */}
            <div className="card section-stack">
                <div className="card-header">
                    <h3 className="card-title">Fragen in dieser Show</h3>
                    <div className="chip-row">
                        <span className="chip muted">{questionsForShow.length} Fragen</span>
                        <Link to="/dashboard/fragen" className="btn btn-ghost btn-sm">
                            Im Fragenkatalog bearbeiten
                        </Link>
                    </div>
                </div>
                {questionsForShow.length === 0 ? (
                    <div className="text-muted">
                        Noch keine Fragen zugeordnet. Im Fragenkatalog Fragen dieser Show zuweisen.
                    </div>
                ) : (
                    <ul className="show-edit-questions-grid">
                        {questionsForShow.map((q) => (
                            <li key={q.id} className="show-edit-question-card">
                                <div className="show-edit-question-text">{q.frage}</div>
                                <div className="show-edit-question-meta">
                                    {q.kategorie || '—'} · Schwierigkeit {q.schwierigkeitsgrad ?? '—'}
                                    {q.lastPlayedAt ? ` · zuletzt: ${new Date(q.lastPlayedAt).toLocaleDateString()}` : ''}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Section 3: Matrix-Vorschau (read-only) */}
            <div className="card section-stack">
                <div className="card-header">
                    <h3 className="card-title">Matrix-Anordnung</h3>
                    <div className="chip-row">
                        <span className="chip muted">
                            {matrixStats.filled} / {matrixStats.rows}×{matrixStats.cols}
                        </span>
                        <Link to="/dashboard/matrix" className="btn btn-ghost btn-sm">
                            Matrix bearbeiten
                        </Link>
                    </div>
                </div>
                {!Array.isArray(matrix) || matrixStats.rows === 0 || (matrixStats.rows === 1 && matrixStats.cols === 1 && matrixStats.filled === 0) ? (
                    <div className="text-muted">
                        Noch keine Matrix-Belegung vorhanden. Im Bereich „Matrix“ in der Navigation bearbeiten
                        (Show muss aktiv sein).
                    </div>
                ) : (
                    <MatrixGrid matrix={matrix} colCount={matrixStats.cols} />
                )}
                {!isActive && (
                    <div className="text-muted" style={{ fontSize: '0.85em' }}>
                        Hinweis: Der Matrix-Editor in der Navigation arbeitet immer auf der aktiven Show.
                        Diese Show ist aktuell nicht aktiv.
                    </div>
                )}
            </div>
        </div>
    );
}

export default ShowEditView;
